"use client";

import { className as classNameHelper } from "@/composites/foundation/utilities/className";
import {
	Button,
	type ButtonProps,
	CartButton,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Icon,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	MenuDropdown,
	SearchButton,
	Text
} from "@pamperedchef/design-system";
import React, { useEffect, useRef, useState } from "react";

import * as styles from "./Header.module.scss";
import { MobileMenu, type MobileMenuProps } from "./MobileMenu";
import { Search, type SearchProps } from "./Search";
import { UtilityLinks, type UtilityLinksProps } from "./UtilityLinks";

export interface ContactConsultantInfo {
	consultant: {
		name: string;
	};
}

type NavigationItem<LinkComponentProps = DefaultLinkProps> =
	| { type: "link"; link: LinkProps<LinkComponentProps> }
	| {
			type: "menu";
			label: string;
			items: LinkProps<LinkComponentProps>[];
	  };

export interface HeaderProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	logo: ImageProps<ImageComponentProps>;
	logoLink: LinkProps<LinkComponentProps>;
	utilityLinks: UtilityLinksProps<LinkComponentProps, ImageComponentProps>;
	navItems: NavigationItem<LinkComponentProps>[];
	cart: {
		cta: ButtonProps;
		itemNumber?: number;
	};
	search: SearchProps;
	consultantImage?: ImageProps<ImageComponentProps>;
	contactConsultantInfo?: ContactConsultantInfo | undefined;
	mobileNav: MobileMenuProps<LinkComponentProps, ImageComponentProps>;
	onContactConsultantClick?: () => void;
	onMyShowsClick?: () => void;
}

// Desktop docks only the nav row: the sticky header is offset by the
// utility row's height so that row scrolls away first.
function useDockedHeader() {
	const headerRef = useRef<HTMLDivElement>(null);
	const topRef = useRef<HTMLDivElement>(null);
	const [docked, setDocked] = useState(false);

	useEffect(() => {
		const header = headerRef.current;
		const top = topRef.current;
		if (!header || !top) return;

		const root = document.documentElement;
		const resizeObserver = new ResizeObserver(() => {
			header.style.setProperty(
				"--header-top-height",
				`${top.offsetHeight}px`
			);
			const stickyTop = parseFloat(getComputedStyle(header).top) || 0;
			root.style.setProperty(
				"--header-docked-height",
				`${header.offsetHeight + stickyTop}px`
			);
		});
		const intersectionObserver = new IntersectionObserver(
			([entry]) => {
				setDocked(
					!entry.isIntersecting && entry.boundingClientRect.top < 0
				);
			},
			{ rootMargin: "-1px 0px 0px 0px" }
		);
		resizeObserver.observe(header);
		resizeObserver.observe(top);
		intersectionObserver.observe(top);

		return () => {
			resizeObserver.disconnect();
			intersectionObserver.disconnect();
			root.style.removeProperty("--header-docked-height");
		};
	}, []);

	return { headerRef, topRef, docked };
}

/**
 * Header component
 */
export function Header<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	logo,
	logoLink,
	utilityLinks,
	navItems,
	cart,
	search,
	consultantImage,
	contactConsultantInfo,
	mobileNav,
	onContactConsultantClick,
	onMyShowsClick
}: HeaderProps<LinkComponentProps, ImageComponentProps>): JSX.Element {
	const [showMobileSearch, setMobileSearch] = useState(false);
	const { headerRef, topRef, docked } = useDockedHeader();

	const [showConsultantCard, setShowConsultantCard] = useState(true);
	const consultantName = contactConsultantInfo?.consultant.name
		? contactConsultantInfo.consultant.name
		: "Consultant Name";

	const cartItemCount = cart.itemNumber ?? 0;
	const cartAriaLabel =
		cartItemCount > 0
			? `Cart, ${cartItemCount} item${cartItemCount === 1 ? "" : "s"}`
			: "Cart, empty";

	return (
		<div
			ref={headerRef}
			className={styles["header"]}
			data-docked={docked || undefined}
		>
			<Container
				ref={topRef}
				className={styles["header-top"]}
				containerClassName={styles["container-top"]}
			>
				<div className={styles["logo-wrapper"]}>
					<Link<LinkComponentProps>
						{...logoLink}
						id={"site-logo-desktop"}
					>
						<Image<ImageComponentProps>
							objectFit="contain"
							{...logo}
							{...classNameHelper(
								styles["logo"],
								styles["desktop"],
								logo.className
							)}
							alt={logo.alt || ""}
						/>
					</Link>
				</div>
				<UtilityLinks
					{...utilityLinks}
					onMyShowsClick={onMyShowsClick}
				/>
			</Container>
			<Container
				className={styles["header-bottom"]}
				containerClassName={styles["container-bottom"]}
			>
				<nav
					{...classNameHelper(
						styles["navigation"],
						styles["desktop"]
					)}
				>
					{navItems.map((navItem, idx) => {
						if (navItem.type === "menu") {
							const mappedItems = navItem.items.map((item) => {
								const linkItem =
									item as LinkProps<LinkComponentProps> & {
										href?: string;
									};
								return {
									...linkItem,
									label: linkItem.children as
										string | undefined,
									href: linkItem.href
								};
							});
							return (
								<MenuDropdown
									key={idx}
									className={styles["menu-dropdown"]}
									items={
										mappedItems as unknown as React.ComponentProps<
											typeof MenuDropdown
										>["items"]
									}
									icon={{ name: "ChevronDown" }}
									expandedIcon={{ name: "ChevronUp" }}
								>
									{navItem.label}
								</MenuDropdown>
							);
						}

						return (
							<Link<LinkComponentProps>
								key={idx}
								{...navItem.link}
								size="md"
							/>
						);
					})}

					<div className={styles["search-container"]}>
						<Button
							{...cart.cta}
							{...classNameHelper(
								styles["cart-button"],
								cart.cta.className
							)}
							aria-label={cartAriaLabel}
							kind="tertiary"
						>
							<Icon name="cart" aria-hidden="true" />
							{cart.itemNumber ? (
								<Text
									{...classNameHelper(
										styles["cart-item-number"],
										{
											[styles["three-digits"]]:
												cart.itemNumber >= 100
										}
									)}
									tag="span"
									styledAs="xxsb"
								>
									{cart.itemNumber}
								</Text>
							) : undefined}
						</Button>
						<Search {...search} />
						{consultantImage && (
							<div className={styles["consultant-wrapper"]}>
								<Button
									kind="tertiary"
									className={
										styles["consultant-avatar-button"]
									}
									aria-label={
										showConsultantCard
											? "Hide consultant details"
											: "Show consultant details"
									}
									aria-expanded={showConsultantCard}
									onPress={() =>
										setShowConsultantCard(
											!showConsultantCard
										)
									}
								>
									<Image<ImageComponentProps>
										{...consultantImage}
										{...classNameHelper(
											styles["consultant-image"],
											consultantImage.className
										)}
										aspectRatio="1:1"
										borderRadius="full"
										width={65}
										height={65}
										alt=""
									/>
								</Button>
								{showConsultantCard && (
									<div className={styles["consultant-card"]}>
										<div
											className={
												styles["consultant-card-header"]
											}
										>
											<button
												type="button"
												className={
													styles[
														"consultant-card-close-button"
													]
												}
												aria-label="Close consultant details"
												onClick={() =>
													setShowConsultantCard(false)
												}
											>
												<Icon
													name="X"
													size={12}
													className={
														styles["close-icon"]
													}
												/>
											</button>
										</div>
										<div
											className={
												styles["consultant-card-body"]
											}
										>
											<Text styledAs="xsl" tag="span">
												Consultant
											</Text>
											<Text styledAs="xsr" tag="span">
												{consultantName}
											</Text>
											<Button
												kind="secondary"
												size="sm"
												onPress={() => {
													setShowConsultantCard(
														false
													);
													Promise.resolve().then(
														() => {
															onContactConsultantClick?.(); // Then open modal safely after microtask
														}
													);
												}}
											>
												Contact Me
											</Button>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</nav>
				<div
					{...classNameHelper(
						styles["logo-wrapper"],
						styles["mobile"]
					)}
				>
					<Link<LinkComponentProps>
						{...logoLink}
						id={"site-logo-mobile"}
					>
						<Image<ImageComponentProps>
							{...logo}
							{...classNameHelper(
								styles["logo"],
								styles["mobile"],
								logo.className
							)}
							alt={logo.alt || ""}
						/>
					</Link>
				</div>
				<nav
					{...classNameHelper(styles["navigation"], styles["mobile"])}
				>
					<MobileMenu
						{...mobileNav}
						onMyShowsClick={onMyShowsClick}
					/>
					<SearchButton
						{...classNameHelper(
							styles["mobile-button"],
							styles["mobile-search-button"]
						)}
						size="md"
						aria-label="Search"
						onClick={() => setMobileSearch(!showMobileSearch)}
					/>
					<CartButton
						className={styles["mobile-button"]}
						{...cart.cta}
						itemNumber={cart.itemNumber || undefined}
						aria-label={cartAriaLabel}
					/>
				</nav>
			</Container>
			<div
				{...classNameHelper(styles["mobile-search-container"], {
					[styles["active"]]: showMobileSearch
				})}
			>
				<Search {...search} />
			</div>
		</div>
	);
}
